/**
 * Campaign State Manager (Generic)
 *
 * Generic campaign state manager supporting any action ID type.
 * Manages campaign state tracking and synchronization with Win32 client.
 * Provides methods for action completion, campaign updates, and completion notifications.
 *
 * Key Design:
 * - Generic type parameter TActionId supports any enum or string union
 * - Configuration-based: action mapping and completion logic provided via CampaignConfig
 * - Flat completedActions array (no CampaignGuid filtering)
 * - When last action is completed, sends TWO events:
 *   1. CampaignStateUpdate with all completedActions
 *   2. CampaignCompletion with completed: true
 *
 * @template TActionId - The action ID type (enum, string union, etc.)
 */

import {
    CampaignStatePayload,
    CampaignStateUpdatePayload,
    CampaignCompletionPayload,
    CampaignConfig,
    validateAndNormalizePayload
} from '../types/CampaignTypes';
import { ProgrammableSurfacesCommunication } from './ProgrammableSurfacesCommunication';
import { logUserAction, logWarning } from '../telemetry';
import { ProgrammableSurfacesEvents, ProgrammableSurfacesEvent, ProgrammableSurfacesEventWithoutPayload } from './types';

/**
 * Generic Campaign State Manager
 *
 * Instance-based service responsible for:
 * - Tracking current campaign state (completed actions)
 * - Sending campaign updates to Win32 when actions are completed
 * - Sending campaign completion event when all actions are done
 * - Implementing local-wins conflict resolution
 *
 * Generic Design:
 * - TActionId can be any enum or string union type
 * - Campaign-specific logic (action mapping, completion check) provided via config
 * - No hardcoded dependencies on specific campaigns or action types
 *
 * @template TActionId - The action ID type for this campaign (enum, string union, etc.)
 */
export class CampaignStateManager<TActionId extends string | number = string | number> {
    /**
     * Campaign configuration defining action mapping and completion logic.
     */
    private readonly config: CampaignConfig<TActionId>;

    /**
     * Set of completed action IDs for this campaign.
     * Stored as Set for efficient lookup and deduplication.
     */
    private completedActions: Set<TActionId> = new Set();

    /**
     * Flag to track if user has interacted before receiving Win32 state.
     * Used for local-wins conflict resolution.
     */
    private hasUserInteracted: boolean = false;

    /**
     * Flag to prevent duplicate campaign completion events.
     * Once true, sendCampaignCompletion() becomes a no-op.
     */
    private isCompletionSent: boolean = false;

    /**
     * Communication singleton for sending events to Win32.
     */
    private communication: ProgrammableSurfacesCommunication<ProgrammableSurfacesEvents, unknown>;

    /**
     * Creates a new CampaignStateManager instance for a specific campaign.
     *
     * @param config - Campaign configuration defining actions, mapping, and completion logic
     *
     * @example
     * const config: CampaignConfig<ActionId> = {
     *     componentId: 'trial-countdown',
     *     requiredActions: [ActionId.Action1, ActionId.Action2],
     *     actionMapper: (id) => mapActionId(id)
     * };
     * const manager = new CampaignStateManager(config);
     */
    constructor(config: CampaignConfig<TActionId>) {
        this.config = config;
        this.communication = ProgrammableSurfacesCommunication.getInstance();
    }

    /**
     * Initializes campaign state from Win32.
     * Extracts completedActions and populates the set.
     * Implements local-wins: ignores incoming if user already interacted.
     *
     * @param payload - CampaignStatePayload from Win32 containing completedActions
     */
    initializeCampaignState(payload: CampaignStatePayload): void {
        // Ignore incoming state if user already interacted (local wins)
        if (this.hasUserInteracted) {
            logWarning(`[CampaignStateManager:${this.config.componentId}]`, 'Ignoring incoming state - user already interacted (local-wins)');
            return;
        }

        // Validate and normalize the payload
        const normalizedActions = validateAndNormalizePayload(payload);

        // Map completed actions from schema strings to typed action IDs using config mapper
        this.completedActions.clear();
        normalizedActions.forEach(actionIdStr => {
            const actionId = this.config.actionMapper(actionIdStr);
            if (actionId !== null) {
                this.completedActions.add(actionId);
            }
        });

        console.log(
            `[CampaignStateManager:${this.config.componentId}] Initialized with ${this.completedActions.size} completed actions`
        );
    }

    /**
     * Gets the current set of completed actions for this campaign.
     *
     * @returns Set of completed action IDs (copied to prevent external mutation)
     */
    getCompletedActions(): Set<TActionId> {
        return new Set(this.completedActions);
    }

    /**
     * Checks if a specific action is completed.
     *
     * @param actionId - Action to check
     * @returns true if action is completed
     */
    isActionCompleted(actionId: TActionId): boolean {
        return this.completedActions.has(actionId);
    }

    /**
     * Completes an action and sends update to Win32.
     * Implements idempotency: does nothing if action is already completed.
     * Checks for campaign completion after adding action.
     * On final action, sends TWO events: StateUpdate then Completion.
     *
     * @param actionId - Action to mark as completed
     */
    completeAction(actionId: TActionId): void {
        // Mark user interaction for local-wins
        this.hasUserInteracted = true;

        // Idempotency check: skip if already completed
        if (this.completedActions.has(actionId)) {
            console.log(`[CampaignStateManager:${this.config.componentId}] Action ${actionId} already completed (idempotent)`);
            return;
        }

        // Add action to completed set (deduplication handled by Set)
        this.completedActions.add(actionId);

        logUserAction('CampaignActionCompleted', {
            actionId: String(actionId),
            componentId: this.config.componentId,
            completedCount: this.completedActions.size,
            totalCount: this.config.requiredActions.length
        });

        // Send update to Win32
        this.sendCampaignUpdate();

        // Check if campaign is now complete - send completion event after state update
        if (this.isCampaignComplete()) {
            this.sendCampaignCompletion();
        }
    }

    /**
     * Checks if the campaign is complete.
     * Uses custom completion logic from config.isComplete() if provided,
     * or defaults to checking all required actions are completed.
     *
     * @returns true if campaign is complete
     */
    isCampaignComplete(): boolean {
        // Use custom completion logic if provided
        if (this.config.isComplete) {
            return this.config.isComplete(this.completedActions);
        }

        // Default: check all required actions are completed
        return this.config.requiredActions.every(action => this.completedActions.has(action));
    }

    /**
     * Sends campaign state update to Win32.
     * Serializes action IDs using config.serializeActionId() or defaults to .toString().
     */
    sendCampaignUpdate(): void {
        // Serialize action IDs to strings for Win32 payload
        const serializeAction = this.config.serializeActionId || ((id: TActionId) => id.toString());
        const completedActionsArray = Array.from(this.completedActions).map(serializeAction);

        const payload: CampaignStateUpdatePayload = {
            data: {
                completedActions: completedActionsArray
            }
        };

        const event: ProgrammableSurfacesEvent<
            ProgrammableSurfacesEvents.UpdateCampaignState,
            CampaignStateUpdatePayload
        > = {
            eventType: ProgrammableSurfacesEvents.UpdateCampaignState,
            payload: payload
        };

        this.communication.sendEvent(event);
        console.log(`[CampaignStateManager:${this.config.componentId}] Campaign update sent`);
    }

    /**
     * Sends campaign completion event to Win32.
     * Only sends once per campaign lifecycle (idempotent).
     * Payload is a simple boolean signal: { completed: true }
     */
    sendCampaignCompletion(): void {
        // Prevent duplicate completion events
        if (this.isCompletionSent) {
            logWarning(`[CampaignStateManager:${this.config.componentId}]`, 'Campaign completion already sent (idempotent)');
            return;
        }

        const payload: CampaignCompletionPayload = {
            completed: true
        };

        const event: ProgrammableSurfacesEvent<
            ProgrammableSurfacesEvents.CampaignCompleted,
            CampaignCompletionPayload
        > = {
            eventType: ProgrammableSurfacesEvents.CampaignCompleted,
            payload: payload
        };

        this.communication.sendEvent(event);
        this.isCompletionSent = true;

        logUserAction('CampaignCompleted', { componentId: this.config.componentId });
    }

    /**
     * Requests campaign state from Win32.
     * Sends GetCampaignState event without payload.
     * Win32 will respond with CampaignStateResponse event.
     */
    requestCampaignState(): void {
        const event: ProgrammableSurfacesEventWithoutPayload<ProgrammableSurfacesEvents.GetCampaignState
        > = {
            eventType: ProgrammableSurfacesEvents.GetCampaignState
        };

        this.communication.sendEvent(event);
        console.log('[ProgrammableSurfacesCommunication] Campaign state requested');
    }
}
